import { Button, Checkbox, InfoBox, NumberInput, Select, useFilePicker } from "@tredespace/ui/widgets";
import type { JSX } from "react";
import { setHud } from "../../state/hud/hud.actions.ts";
import {
  goToReference,
  importReference,
  removeReference,
  setReferenceCentre,
  setReferenceCollide,
  setReferenceOpacity,
  setReferenceUnit,
  UNIT_OPTIONS,
} from "../../state/reference/reference.actions.ts";
import { referenceState } from "../../state/reference/reference.state.ts";
import { Field } from "./Field.tsx";

/** Import and place the building you are scaffolding around. */
export function ReferencePanel(): JSX.Element {
  const state = referenceState.use();
  const picker = useFilePicker(".glb,.gltf", (file) => {
    void importReference(file).then((result) => {
      if (result.error) {
        setHud(result.error.msg);
      }
    });
  });

  return (
    <div className="flex flex-col gap-3 overflow-y-auto p-3">
      {picker.element}

      <div className="flex gap-2">
        <Button onClick={picker.open} disabled={state.loading}>
          {state.loading ? "Importing…" : "Import GLB"}
        </Button>
        <Button onClick={goToReference}>Go to model</Button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Field label="File unit">
          <Select
            value={String(state.unit)}
            onChange={(value) => value && setReferenceUnit(Number(value))}
            options={UNIT_OPTIONS.map((entry) => ({ value: String(entry.value), label: entry.label }))}
          />
        </Field>
        <Field label="Opacity %">
          <NumberInput
            value={state.opacity}
            onChange={setReferenceOpacity}
            min={10}
            max={100}
            step={5}
            unit="%"
          />
        </Field>
      </div>

      <Checkbox
        checked={state.centre}
        onChange={setReferenceCentre}
        label="Move to origin on import"
        info={
          "Off by default. The model keeps the world coordinates it was exported at, so a scaffold " +
          "built against it stays in the same frame and drops back into the source model correctly. " +
          "Turning this on discards that placement."
        }
      />
      <Checkbox
        checked={state.collide}
        onChange={setReferenceCollide}
        label="Standards can stand on the model"
        hint="roofs, decks"
      />

      <Button onClick={removeReference} disabled={!state.info}>
        Remove model
      </Button>

      {state.error && <InfoBox className="text-amber-300">{state.error}</InfoBox>}

      <p className="text-xs leading-relaxed text-slate-400">
        {state.info ??
          "No model loaded. glTF 2.0 and GLB are supported, including textures, Draco and the common KHR extensions. " +
            "Models keep their exported position, and the ground patch moves to sit under them."}
      </p>
    </div>
  );
}
